/**
 * Umpire Appointments — `/appointments/umpires` (module 02, `03-appointments.md` Part B).
 *
 * **The scope is the tournament, not a day, and that is the difference from module 01.** §A1
 * makes the day the unit of work on the technical side because the TD appoints a day at a
 * time. §B1 gives him a different job here — *"check the appointments break no rules, and that
 * opportunities are spread fairly"* — and fairness measured over one day is not fairness. So
 * this page reads the whole panel in one request and each of §B3's seven panels narrows what
 * it draws (Part C invariant 2).
 *
 * **The TMS half shows nothing until the TD presses `Load Appointments`.** Part C's states table
 * puts that half's initial state at *"Fetch not yet run — panels show `Not Fetched This
 * Session`"*, and §B4 puts the button on the panel. A TMS half that had quietly filled itself
 * on the way in would make the stamp — the one thing on the banner saying *when* what is on
 * screen was true — a statement about a moment nobody chose.
 *
 * **Decision #13 (§B4a, 16 September 2026) — the half says what it is.** The press reads this
 * system's own record and never TMS, so the button is `Load Appointments`, the half is `As
 * Recorded In This System` and the state before the press is `Not Loaded This Session`.
 *
 * **The AI half's stored draft loads on open, from this system** (§Panel 5a, amended
 * 2026-09-15, RUN 81). The panel reads this system's own record once as it mounts and draws
 * the advisory draft already held there, so the TD sees his yardstick without a press — and is
 * never told to press `Draft The Pool Stage` over a draft he cannot see, which is what the page
 * did while it read nothing until the fetch. The TMS half of that same answer is held back: it
 * still waits for `Load Appointments`, which reads again and stamps.
 *
 * Two supporting fetches run on mount as well, both independent of either half:
 *  · the roster, which the §B10 picker and every panel row need. If it fails the page still
 *    draws and says plainly that no umpire can be chosen until it loads;
 *  · the schedule, read for one thing only — which matches TMS reports as played, which is the
 *    `completed` half of §B8's `completed / appointed`. If it fails, that figure is reported as
 *    unmeasured rather than guessed from a clock.
 */

import { fetchFixtureDays, fetchOfficials } from "../api/client.js";
import type { FixtureListBody, OfficialView } from "../api/types.js";
import { HowToLink, HowToUse } from "../app/HowToUse.js";
import { FailBanner } from "../app/states.js";
import { useAsync } from "../app/useAsync.js";
import { UmpireAppointmentsPanel } from "../features/umpires/UmpireAppointmentsPanel.js";

/**
 * The matches TMS reports as played — §B8's `completed`.
 *
 * `official` and `completed` are the two statuses that mean the match has been played;
 * everything else, `live` included, means it has not finished. Nothing here reads a clock: a
 * match that should have kicked off an hour ago and did not is still not a completed duty.
 */
export function completedMatchIds(body: FixtureListBody): Set<number> {
  return new Set(
    body.fixtures
      .filter((fixture) => fixture.tmsStatus === "official" || fixture.tmsStatus === "completed")
      .map((fixture) => fixture.tmsMatchDbId),
  );
}

export function UmpireAppointmentsPage(): React.JSX.Element {
  const roster = useAsync(() => fetchOfficials(), []);
  const schedule = useAsync(() => fetchFixtureDays(), []);

  const officials: readonly OfficialView[] =
    roster.status === "ready" ? roster.data.officials : [];
  const completed =
    schedule.status === "ready" ? completedMatchIds(schedule.data) : new Set<number>();

  return (
    <>
      <div className="repband">
        <h2>Umpire Appointments</h2>
        <HowToUse id="umpire-appointments" title="How to use Umpire Appointments">
          <li>
            You are not appointing here. The <b>Umpires Managers</b> make umpire appointments in
            TMS; this page is your oversight of them, and nothing on the teal half can be
            changed from it.
          </li>
          <li>
            Press <HowToLink to="fetch-tms">Load Appointments</HowToLink> to show the umpire
            appointments recorded in this system. This page does not read TMS itself, so what
            it shows is what this system holds. The stamp in the banner says when you loaded
            them; until you press it, the page says <b>Not Loaded This Session</b> rather than
            showing figures whose age you cannot tell.
          </li>
          <li>
            Read the chip beside <b>As Recorded In This System</b> first. It counts rule
            violations across what you loaded — an umpire holding two roles in one match, or
            appointed to a match involving their own country. Each offending cell is bold red
            with the rule named beside it. You cannot correct one here; raise it with the
            Umpires Managers.
          </li>
          <li>
            The <b>Appointment Matrix</b> and <b>Teams Coverage</b> below it are counted live
            from those appointments, so loading again after a correction moves them with it.
            On-field, reserve and video duties are never summed into one figure — that is the
            whole point of the table.
          </li>
          <li>
            Press <HowToLink to="on-field-only">On Field Only</HowToLink> on either matrix to
            count only on-pitch pairs — U1 with U2 — in <b>Appointed Together With</b>, which is
            how to check that pairings rotate without reserve and video appointments in the
            way. <b>Duties By Role</b> is left exactly as it is; press it again to go back.
          </li>
          <li>
            The orange half is the engine&rsquo;s draft, and it has two presses.{" "}
            <HowToLink to="draft-all">Draft All</HowToLink> fills U1, U2, RU and VU for{" "}
            <b>every match in the schedule</b>;{" "}
            <HowToLink to="draft-pool-stage">Draft The Pool Stage</HowToLink> fills the{" "}
            <b>pool matches only</b> and leaves everything after them blank. Each asks first
            and says which matches it will reach; both keep any cell you have adjusted by hand.
            Either way it is advisory: nothing on this half is ever pushed anywhere, and it
            does not touch the appointments above.
          </li>
          <li>
            The orange half opens with the draft already stored in this system, so there is
            nothing to press to see it. Its draft buttons wake once that draft has loaded, and a
            draft press replaces it — every engine-drafted cell it reaches is drawn again, and
            only the cells you have adjusted by hand are kept.
          </li>
          <li>
            Click a name on the orange half to try a different umpire — a what-if, and nothing
            more. Anyone from a competing country, already in that match, already on-field that
            day, or flagged <b>Avoid VU</b> for a video slot, is shown greyed with the reason
            beside them and cannot be chosen.
          </li>
          <li>
            The <b>Evenness at a glance</b> note under the orange matrix is the comparison worth
            reading: how many umpires each set of appointments uses, and the heaviest load any
            one of them carries. Where the engine spreads wider, it says so.
          </li>
          <li>
            <HowToLink to="clear-drafts">Clear Drafts</HowToLink> empties the orange half so you
            can run the yardstick again. It asks first, and it removes your own adjustments too.
          </li>
        </HowToUse>
      </div>

      {roster.status === "error" ? (
        <div className="warnnote warnnote--attention" role="status">
          <span className="warnnote__kind">Roster Not Loaded</span>
          <span className="warnnote__msg">
            The panels below are shown as fetched, but no umpire can be chosen on the reference
            half until the roster loads: {roster.error.message}
          </span>
        </div>
      ) : null}
      {schedule.status === "error" ? <FailBanner error={schedule.error} /> : null}

      <UmpireAppointmentsPanel
        officials={officials}
        completedMatchIds={completed}
        scheduleLoaded={schedule.status === "ready"}
      />
    </>
  );
}
